import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * A component that restricts access to routes based on user roles
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {Array<string>} props.allowedRoles - List of roles allowed to access this route
 * @param {string} [props.redirectTo="/login"] - Where to redirect if unauthorized
 */
const RoleBasedRoute = ({ children, allowedRoles, redirectTo = "/login" }) => {
  const { isAuthenticated, auth, isLoading } = useAuth();

  // Show nothing while still loading auth state
  if (isLoading) {
    return <div>Loading authentication...</div>;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} />;
  }

  // Handle case where token exists but user info might not be fully loaded yet
  if (!auth?.user) {
    console.error("User object is missing from auth context");
    return <div>Loading user information...</div>;
  }

  // Get the user's role from the token
  const userRole = auth.user.role;
  console.log("User role:", userRole);
  console.log("Allowed roles:", allowedRoles);
  
  // Special case for ADMIN role - they can access everything
  if (userRole === 'ADMIN') {
    console.log("User is ADMIN, granting access");
    return children;
  }
  
  // Check if the user's role is in the allowed roles list
  const isAuthorized = allowedRoles.includes(userRole);
  console.log("Is authorized:", isAuthorized);
  
  if (!isAuthorized) {
    // Redirect to unauthorized page if not authorized
    console.error(`Access denied: ${userRole} not in allowed roles: ${allowedRoles.join(', ')}`);
    return <Navigate to="/unauthorized" />;
  }
  
  return children;
};

export default RoleBasedRoute;
